<template>
  <div class="questionnaire-signature row visible-on visible-on col-sm-5 col-md-8 col-lg-5 col-sm-offset-3 col-md-offset-1 col-lg-offset-4">
    <label class="form-label">{{ label }}</label>
    <div class="signature-content">
      <div v-if="isMobile" class="signature-preview mobile" @click="onMobileSignClick">
        <img v-if="mobilePreviewSrc" :src="mobilePreviewSrc" alt="signature">
        <span v-else>{{ $t('questionnaire.label.clickToSign') }}</span>
        <button v-if="hasMobileSignaturePreview && canEdit" type="button" class="resign-button" @click.stop="onMobileResignClick">{{ $t('questionnaire.label.resign') }}</button>
      </div>
      <template v-else>
        <div v-if="value && !isEditing" class="signature-preview">
          <img :src="getFilePath(value)" alt="signature">
          <button v-if="canEdit" type="button" class="resign-button" @click="onResignClick">{{ $t('questionnaire.label.resign') }}</button>
        </div>
        <div v-else-if="canEdit" class="signature-pad-frame">
          <SignaturePad ref="signaturePad" width="100%" height="210px" />
        </div>
      </template>
    </div>
    <div v-show="showError" class="signature-error">{{ $t('questionnaire.error.signatureRequired') }}</div>
    <div v-if="showSignDialog" class="sign-pad">
      <div class="bottom">
        <div class="footer-btn">
          <div class="btn cancel" @click="handleCancelSign">
            <span class="label">{{ $t('common.btn.cancel') }}</span>
          </div>
          <div class="btn submit" @click="handleSaveSign">
            <span class="label">{{ $t('common.btn.submit') }}</span>
          </div>
        </div>
      </div>
      <SignaturePad needRotate ref="signRef" :width="`${signWidth}px`" :height="`${signHeight - 60}px`"></SignaturePad>
    </div>
  </div>
</template>

<script>
import SignaturePad from '@/components/common/sign/signaturePad.vue'
import { deviceType } from '@/utils/common'
import { getFilePath, uploadBase64Src } from '@/utils/qiniu'

export default {
  components: {
    SignaturePad
  },
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: '' },
    isRequired: { type: [Boolean, Number], default: false },
    candidateId: { type: [String, Number], default: null },
    jobId: { type: [String, Number], default: null },
    questionId: { type: [String, Number], default: null },
    isEditable: { type: String, default: '' }
  },
  data () {
    return {
      isEditing: !this.value,
      showError: false,
      showSignDialog: false,
      originalValue: this.value,
      originalSavedValue: this.value,
      savedValue: this.value,
      originalPreviewSrc: '',
      previewSrc: '',
      bodyOverflow: '',
      htmlOverflow: '',
      isPageScrollLocked: false,
      signWidth: 0,
      signHeight: 0
    }
  },
  computed: {
    isMobile () {
      return deviceType.isMobile
    },
    canEdit () {
      return this.isEditable !== 'false'
    },
    hasMobileSignaturePreview () {
      return Boolean(this.value || this.savedValue || this.previewSrc)
    },
    mobilePreviewSrc () {
      return this.previewSrc || this.getFilePath(this.value || this.savedValue)
    }
  },
  watch: {
    value (value) {
      this.savedValue = value
      if (value) {
        this.isEditing = false
        this.showError = false
      } else if (!this.showSignDialog) {
        this.previewSrc = ''
      }
    }
  },
  methods: {
    getFilePath,
    lockPageScroll () {
      if (this.isPageScrollLocked) {
        return
      }
      this.bodyOverflow = document.body.style.overflow
      this.htmlOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
      this.isPageScrollLocked = true
    },
    unlockPageScroll () {
      if (!this.isPageScrollLocked) {
        return
      }
      document.body.style.overflow = this.bodyOverflow
      document.documentElement.style.overflow = this.htmlOverflow
      this.isPageScrollLocked = false
    },
    refreshSignSize () {
      const viewport = window.visualViewport
      const viewportWidth = viewport ? viewport.width : document.documentElement.clientWidth
      const viewportHeight = viewport ? viewport.height : document.documentElement.clientHeight

      this.signWidth = viewportWidth - 84 - 24
      this.signHeight = viewportHeight
    },
    validate () {
      const signaturePad = this.$refs.signaturePad
      const isValid = Boolean(this.value || this.savedValue) || (signaturePad && !signaturePad.isEmpty())
      this.showError = Boolean(this.isRequired) && !isValid
      return !this.showError
    },
    async persist () {
      if (!this.isEditing && (this.savedValue || this.value)) {
        return this.savedValue || this.value
      }
      const signaturePad = this.$refs.signaturePad
      if (!signaturePad || signaturePad.isEmpty()) {
        return ''
      }
      return this.persistSignature(signaturePad)
    },
    async persistSignature (signaturePad, base64Src) {
      if (!this.candidateId || !this.jobId || !this.questionId) {
        throw new Error('Missing signature upload context')
      }
      if (!base64Src) {
        base64Src = await signaturePad.toDataURL()
      }
      const fileName = `${this.candidateId}_${this.jobId}_${this.questionId}_${new Date().valueOf()}_sign.png`
      const fileKey = `jobApplication/candidateSign/${this.candidateId}/${this.jobId}/${this.questionId}/${fileName}`
      const resp = await uploadBase64Src(fileKey, base64Src)
      if (!resp || !resp.key) {
        throw new Error('Signature upload failed')
      }
      const fileUrl = getFilePath(resp.key)
      this.savedValue = fileUrl
      this.$emit('input', fileUrl)
      this.isEditing = false
      this.showError = false
      this.previewSrc = fileUrl
      return fileUrl
    },
    onResignClick () {
      this.previewSrc = ''
      this.savedValue = ''
      this.$emit('input', '')
      this.isEditing = true
    },
    onMobileSignClick () {
      if (!this.canEdit || this.hasMobileSignaturePreview) {
        return
      }
      this.openMobileSignDialog()
    },
    onMobileResignClick () {
      this.openMobileSignDialog()
    },
    openMobileSignDialog () {
      this.originalValue = this.value || this.savedValue
      this.originalSavedValue = this.savedValue
      this.originalPreviewSrc = this.previewSrc
      this.showError = false
      this.refreshSignSize()
      this.showSignDialog = true
      this.lockPageScroll()
    },
    handleCancelSign () {
      this.showSignDialog = false
      this.showError = false
      this.previewSrc = this.originalPreviewSrc
      this.savedValue = this.originalSavedValue
      this.$emit('input', this.originalValue)
      this.unlockPageScroll()
    },
    async handleSaveSign () {
      const signaturePad = this.$refs.signRef
      if (!signaturePad || signaturePad.isEmpty()) {
        this.showError = Boolean(this.isRequired)
        return
      }
      try {
        const base64Src = await signaturePad.toDataURL()
        await this.persistSignature(signaturePad, base64Src)
        this.showSignDialog = false
        this.unlockPageScroll()
      } catch (e) {
        this.$emit('upload-error')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.questionnaire-signature {
  .form-label {
    font-size: 14px;
  }
  .signature-content {
    min-height: 210px;
    margin-top: 8px;
  }
  .signature-pad-frame {
    border: 1px solid #ccc;

    ::v-deep .signature-pad {
      border: 0 !important;
    }

    ::v-deep .canvas {
      display: block;
    }
  }
  .signature-preview {
    position: relative;
    min-height: 180px;
    border: 1px solid #dcdfe6;
    background: #fff;
    img {
      display: block;
      width: 100%;
      height: 180px;
      object-fit: contain;
    }
    &.mobile {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #606266;
    }
  }
  .resign-button {
    position: absolute;
    right: 10px;
    bottom: 10px;
    border: 0;
    background: transparent;
    color: #409eff;
    cursor: pointer;
  }
  .signature-error {
    margin-top: 5px;
    color: #f56c6c;
    font-size: 12px;
  }
  .sign-pad {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 9999;
    background-color: #fff;
    overflow: hidden;
    user-select: none;
    touch-action: none;
    .signature-wrapper {
      margin: 30px 24px 30px 0;
      flex: 1;
      min-width: 0;
    }

    .bottom {
      display: flex;
      align-items: center;
      flex: 0 0 84px;
      width: 84px;
      margin: 34px 0 30px;
      flex-direction: column;
      justify-content: flex-end;

      .tip {
        flex: 1;
        color: #606266;
        font-size: 16px;
        white-space: nowrap;
        word-break: break-word;
        text-overflow: ellipsis;
        overflow: hidden;
        padding-top: 8px;
        .label {
          width: 32px;
          display: inline-block;
          transform: rotate(90deg);
        }
      }

      .footer-btn {
        height: 100%;
        position: relative;
        border: 0;
        display: flex;
        margin: 0;
        width: 52px;
        flex-direction: column;
        padding: 0;
        .btn {
          flex: 1;
          margin-top: 10px;
          padding: 10px;
          border-radius: 6px;
          background: #f2f2f2;
          font-size: 16px;
          cursor: pointer;
          word-break: keep-all;
          white-space: nowrap;
          display: flex;
          align-items: center;
          .label {
            width: 32px;
            display: inline-block;
            transform: rotate(90deg);
          }
        }

        .cancel {
          text-align: center;
        }
        .submit {
          color: #ffffff;
          background: #0096cc;
          text-align: center;
        }
      }
    }
  }
}
</style>
